/**
 * Headline-card state for the article images panel.
 *
 * When an article has no usable announcement image the backend renders a
 * per-article 1200×630 card (masthead + headline + ticker + date) and stores
 * it in `prepared_images` under `role: 'headline_card'`; an operator can
 * suppress it per article via `article_metadata.headline_card_suppressed`.
 * The two signals combine into three states for the tile — pure so the
 * derivation is testable without the panel.
 */
import type { PreparedImage } from './api/articles.js';

export const HEADLINE_CARD_ROLE = 'headline_card';

export type HeadlineCardState =
  /** A card exists — `entry` is the SAME object from the array passed in,
   *  so callers can `indexOf` it for the lightbox. */
  | { kind: 'card'; entry: PreparedImage }
  /** No card and the operator suppressed it; the static default card ships. */
  | { kind: 'suppressed' }
  | null;

export function findHeadlineCard(
  images: readonly PreparedImage[] | null | undefined,
): PreparedImage | null {
  return images?.find((i) => i?.role === HEADLINE_CARD_ROLE) ?? null;
}

export function headlineCardState(
  images: readonly PreparedImage[] | null | undefined,
  articleMetadata: Record<string, unknown> | null | undefined,
): HeadlineCardState {
  const entry = findHeadlineCard(images);
  // An entry always wins: the flag on its own says nothing about what is
  // actually stored, and the tile must show what would ship.
  if (entry) return { kind: 'card', entry };
  if (articleMetadata?.headline_card_suppressed === true) return { kind: 'suppressed' };
  return null;
}

/** Announcement images only — the card is not a figure and never counts as one. */
export function announcementImages(images: readonly PreparedImage[]): PreparedImage[] {
  return images.filter((i) => i?.role === 'featured' || i?.role === 'inline');
}
